import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import MonsterSheetViewer from '../MonsterSheetViewer/MonsterSheetViewer';
// @date_of_change 2025-08-30
// Replaced MonsterDataNormalizer with UnifiedDataManager for consistent data access
import UnifiedDataManager from '../../services/UnifiedDataManager';
import HPFormatBridge from '../../utils/HPFormatBridge';
import {
  resolveCombatantChallengeDisplay,
  resolveCombatantProficiencyBonus,
  resolveCombatantSpeedFeet,
  resolvePreferredAbilityScores,
  resolvePreferredArmorClass,
  resolvePreferredInitiative
} from '../../utils/combat/CombatantNormalizer';
import { hydrateCharacterCombatState } from '../../utils/CharacterCombatReadiness';
import './TokenPropertiesPopup.css';
import EffectColors from '../../config/EffectColors';
import TokenPresentationControls from '../battle-room-v2/components/TokenPresentationControls';
import AuthoritativeDamageControls from '../battle-room-v2/components/AuthoritativeDamageControls';
import CreatureEquipmentControls from '../battle-room-v2/components/CreatureEquipmentControls';

// Debug logging helper
const DEBUG = true;
const debugLog = (tag, ...args) => {
  if (DEBUG) console.log(`[TokenPropertiesPopup:${tag}]`, ...args);
};

// Keep the omitted-position value referentially stable. A new object on every
// render retriggered the position synchronization effect, which then wrote a
// new state object and caused an endless render loop.
// date_of_change: 2026-08-21
const DEFAULT_POPUP_POSITION = Object.freeze({ x: 100, y: 100 });

const firstNonEmptyText = (...values) => {
  for (const value of values) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return '';
};

const toFiniteNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (value == null || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toTitleCase = (value) => {
  const text = firstNonEmptyText(value);
  if (!text) return '';
  return text.replace(/\b([a-z])/gi, (match) => match.toUpperCase());
};

const parseFeetValue = (value) => {
  const directNumber = toFiniteNumber(value);
  if (directNumber != null) {
    return directNumber;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const match = value.match(/(-?\d+(?:\.\d+)?)/);
  return match ? Number(match[1]) : null;
};

const clampPopupPosition = (rawPosition) => {
  const fallbackX = Number.isFinite(rawPosition?.x) ? rawPosition.x : 100;
  const fallbackY = Number.isFinite(rawPosition?.y) ? rawPosition.y : 100;

  if (typeof window === 'undefined') {
    return { x: fallbackX, y: fallbackY };
  }

  const viewportWidth = window.innerWidth || 0;
  const viewportHeight = window.innerHeight || 0;
  const estimatedWidth = 450;
  const estimatedHeight = Math.round((viewportHeight || 900) * 0.8);
  const margin = 12;

  return {
    x: viewportWidth
      ? Math.min(Math.max(margin, fallbackX), Math.max(margin, viewportWidth - estimatedWidth - margin))
      : fallbackX,
    y: viewportHeight
      ? Math.min(Math.max(margin, fallbackY), Math.max(margin, viewportHeight - estimatedHeight - margin))
      : fallbackY
  };
};

/**
 * TokenPropertiesPopup Component
 * 
 * A popup window for editing token properties that appears when right-clicking
 * on a token and selecting "Properties". Features:
 * - Scrollable content for all token properties
 * - Live editing with immediate state updates
 * - Persistent data that saves changes automatically
 * - Draggable popup window
 * - Close button and click-outside-to-close functionality
 * 
 * Props:
 * - token: The token object with all properties
 * - visible: Boolean to show/hide the popup
 * - onClose: Function to close the popup
 * - onUpdate: Function to update token properties
 * - onTokenSizeChange: Function to update token size
 * - onTogglePin: Function to toggle pin state
 * - position: {x, y} position for the popup
 */
const TokenPropertiesPopup = ({ 
  token, 
  visible, 
  onClose, 
  onUpdate, 
  onTokenSizeChange, 
  onTogglePin, 
  position = DEFAULT_POPUP_POSITION,
  isGameMaster = false
}) => {
  const initialPopupPosition = useMemo(() => clampPopupPosition(position), [position]);
  const [popupPosition, setPopupPosition] = useState(initialPopupPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isPinned, setIsPinned] = useState(false);
  const [activeMonsterTab, setActiveMonsterTab] = useState('sheet');
  const popupRef = useRef(null);

  // Local state for form inputs
  const [formData, setFormData] = useState({
    name: '',
    type: 'player',
    hp: { current: 0, max: 0 },
    ac: 0,
    initiative: 0,
    speed: 30,
    level: 1,
    size: 'Medium',
    creatureType: '',
    creatureTypeLabel: '',
    alignment: '',
    challengeRating: '',
    strength: 10,
    dexterity: 10,
    constitution: 10,
    intelligence: 10,
    wisdom: 10,
    charisma: 10,
    ...token
  });

  useEffect(() => {
    setActiveMonsterTab(String(token?.type || '').toLowerCase() === 'monster' ? 'sheet' : 'edit');
  }, [token?.id, token?.type]);

  useEffect(() => {
    if (!visible || isDragging) return;
    setPopupPosition(clampPopupPosition(position));
  }, [visible, position, isDragging]);

  // Normalize token data when it changes
  useEffect(() => {
    if (!token) return undefined;

    let cancelled = false;

    const normalizeTokenData = async () => {
      try {
        const normalizedTokenType = String(token.type || '').toLowerCase();

        debugLog('TokenData', 'Processing token:', token.id, token.name, token.type);
        debugLog('TokenData', 'Token structure:', {
          hasEntity: !!token.entity,
          hasHpObj: !!token.hpObj,
          entityHpObj: token.entity?.hpObj,
          tokenHpObj: token.hpObj,
          hp: token.hp,
          maxHp: token.maxHp
        });

        let normalizedData = { ...token };
        let normalizedMonsterEntity = null;

        if (normalizedTokenType === 'monster') {
          debugLog('Normalize', 'Using UnifiedDataManager for monster token');

          const embeddedMonsterEntity = token.originalUnifiedEntity
            || token.monsterData?.originalUnifiedEntity
            || (token.monsterData?.data ? {
              id: token.baseTemplate || token.monsterData?.id || token.templateId || token.entity?.id || token.id,
              type: 'monster',
              data: token.monsterData.data,
              _meta: {}
            } : null);

          if (embeddedMonsterEntity) {
            normalizedMonsterEntity = embeddedMonsterEntity;
            const monsterDisplayData = UnifiedDataManager.getDisplayData(embeddedMonsterEntity);
            normalizedData = {
              ...(embeddedMonsterEntity.data || {}),
              ...monsterDisplayData,
              ...token,
              type: 'monster',
              originalUnifiedEntity: token.originalUnifiedEntity || embeddedMonsterEntity,
              monsterData: token.monsterData || {
                ...(embeddedMonsterEntity.data || {}),
                data: embeddedMonsterEntity.data || {}
              }
            };
          } else {
            const candidateIds = [
              token.entity?.id,
              token.baseTemplate,
              token.monsterData?.id,
              token.originalUnifiedEntity?.id,
              token.templateId
            ].filter(Boolean);

            for (const candidateId of candidateIds) {
              try {
                const monsterEntity = await UnifiedDataManager.getEntityById(candidateId, 'monster');
                if (!monsterEntity) continue;

                normalizedMonsterEntity = monsterEntity;
                const monsterDisplayData = UnifiedDataManager.getDisplayData(monsterEntity);
                normalizedData = {
                  ...(monsterEntity.data || {}),
                  ...monsterDisplayData,
                  ...token,
                  type: 'monster',
                  originalUnifiedEntity: token.originalUnifiedEntity || monsterEntity,
                  monsterData: token.monsterData || {
                    ...(monsterEntity.data || {}),
                    data: monsterEntity.data || {}
                  }
                };
                break;
              } catch (error) {
                debugLog('Normalize', `Failed to load monster entity '${candidateId}':`, error);
              }
            }
          }
        } else {
          debugLog('Normalize', 'Processing player/NPC token');
        }

        if (cancelled) return;

        let hpSource = token;
        if (token.entity && token.entity.hpObj) {
          debugLog('Normalize', 'Using entity HP data from combat updates');
          hpSource = token.entity;
        }
        normalizedData.hpObj = HPFormatBridge.createUnifiedHP(hpSource);

        const normalizedFormType = String(normalizedData.type || normalizedTokenType || 'player').toLowerCase();
        const hydratedCharacterData = normalizedFormType !== 'monster'
          ? hydrateCharacterCombatState(normalizedData)
          : null;
        const monsterSchemaData = normalizedFormType === 'monster'
          ? (
            normalizedMonsterEntity?.data
            || normalizedData.schemaData
            || normalizedData.originalUnifiedEntity?.data
            || token.originalUnifiedEntity?.data
            || token.monsterData?.data
            || token.monsterData
            || {}
          )
          : null;
        const monsterAbilities = normalizedFormType === 'monster'
          ? resolvePreferredAbilityScores({
            runtimeSources: [
              token.abilities,
              token.stats,
              token
            ],
            canonicalSources: [
              monsterSchemaData?.abilities,
              monsterSchemaData?.stats,
              normalizedMonsterEntity?.data?.abilities,
              normalizedMonsterEntity?.data?.stats,
              monsterSchemaData,
              token.monsterData,
              token.originalUnifiedEntity?.data
            ],
            runtimeOverrideSource: token
          })
          : null;
        const resolvedMonsterSpeed = normalizedFormType === 'monster'
          ? resolveCombatantSpeedFeet(
            token,
            normalizedData,
            monsterSchemaData,
            token.monsterData,
            token.originalUnifiedEntity?.data
          )
          : null;
        const resolvedMonsterCreatureType = normalizedFormType === 'monster'
          ? firstNonEmptyText(
            token.creatureTypeLabel,
            token.creatureType,
            normalizedData.creatureTypeLabel,
            normalizedData.creatureType,
            monsterSchemaData?.taxonomy?.type,
            monsterSchemaData?.creatureType?.type,
            monsterSchemaData?.creatureType?.name
          )
          : '';
        const resolvedMonsterAlignment = normalizedFormType === 'monster'
          ? firstNonEmptyText(
            token.alignment,
            normalizedData.alignment,
            monsterSchemaData?.taxonomy?.alignment,
            monsterSchemaData?.alignment
          )
          : '';
        const resolvedMonsterChallengeRating = normalizedFormType === 'monster'
          ? firstNonEmptyText(
            token.challengeRating,
            token.cr,
            normalizedData.challengeRating,
            normalizedData.cr,
            resolveCombatantChallengeDisplay(
              token,
              normalizedData,
              monsterSchemaData,
              token.monsterData,
              token.originalUnifiedEntity?.data
            )
          )
          : '';
        const resolvedMonsterSize = normalizedFormType === 'monster'
          ? toTitleCase(
            firstNonEmptyText(
              token.size,
              normalizedData.size,
              monsterSchemaData?.taxonomy?.size,
              monsterSchemaData?.size
            )
          )
          : '';
        const resolvedCharacterAbilities = normalizedFormType !== 'monster'
          ? resolvePreferredAbilityScores({
            runtimeSources: [
              token.abilities,
              normalizedData.abilities,
              normalizedData.stats,
              normalizedData
            ],
            canonicalSources: [
              token.characterData,
              token.schemaData,
              token.canonicalData,
              token.originalUnifiedEntity?.data,
              normalizedData.characterData,
              normalizedData.schemaData,
              normalizedData.canonicalData,
              normalizedData.data,
              hydratedCharacterData
            ],
            runtimeOverrideSource: token
          })
          : null;
        const resolvedCharacterAc = normalizedFormType !== 'monster'
          ? resolvePreferredArmorClass({
            runtimeSources: [
              token,
              normalizedData
            ],
            canonicalSources: [
              token.characterData,
              token.schemaData,
              token.canonicalData,
              token.originalUnifiedEntity?.data,
              hydratedCharacterData
            ],
            runtimeOverrideSource: token,
            fallbackValue: null
          })
          : null;
        const resolvedCharacterInitiative = normalizedFormType !== 'monster'
          ? resolvePreferredInitiative({
            runtimeSources: [
              token,
              normalizedData
            ],
            canonicalSources: [
              token.characterData,
              token.schemaData,
              token.canonicalData,
              token.originalUnifiedEntity?.data,
              hydratedCharacterData
            ],
            runtimeOverrideSource: token,
            fallbackValue: null
          })
          : null;
        const resolvedCharacterSpeed = normalizedFormType !== 'monster'
          ? resolveCombatantSpeedFeet(
            token,
            normalizedData,
            token.characterData,
            token.schemaData,
            token.canonicalData,
            token.originalUnifiedEntity?.data,
            hydratedCharacterData
          )
          : null;
        const resolvedCharacterProficiencyBonus = normalizedFormType !== 'monster'
          ? resolveCombatantProficiencyBonus(
            token,
            normalizedData,
            token.characterData,
            token.schemaData,
            token.canonicalData,
            token.originalUnifiedEntity?.data,
            hydratedCharacterData
          )
          : null;

        const newFormData = {
          name: '',
          hp: { current: 0, max: 0 },
          ac: 10,
          initiative: 0,
          speed: 30,
          level: 1,
          ...normalizedData,
          type: normalizedFormType,
          statusEffects: (normalizedData && normalizedData.statusEffects) ? { ...normalizedData.statusEffects } : (token && token.statusEffects ? { ...token.statusEffects } : {}),
          statusEffectsMeta: (normalizedData && normalizedData.statusEffectsMeta) ? { ...normalizedData.statusEffectsMeta } : (token && token.statusEffectsMeta ? { ...token.statusEffectsMeta } : {}),
          conditions: Array.isArray(normalizedData?.conditions) ? [...normalizedData.conditions] : (Array.isArray(token?.conditions) ? [...token.conditions] : [])
        };
        newFormData.hp = normalizedData.hpObj || { current: normalizedData.hp || 0, max: normalizedData.maxHp || normalizedData.hp || 0 };
        if (normalizedFormType === 'monster') {
          newFormData.creatureType = resolvedMonsterCreatureType || '';
          newFormData.creatureTypeLabel = resolvedMonsterCreatureType ? toTitleCase(resolvedMonsterCreatureType) : '';
          newFormData.alignment = resolvedMonsterAlignment ? toTitleCase(resolvedMonsterAlignment) : '';
          newFormData.challengeRating = resolvedMonsterChallengeRating || '';
          newFormData.cr = resolvedMonsterChallengeRating || normalizedData.cr || '';
          newFormData.speed = resolvedMonsterSpeed ?? parseFeetValue(normalizedData.speed) ?? 30;
          newFormData.size = resolvedMonsterSize || toTitleCase(normalizedData.size) || 'Medium';
        } else {
          if (resolvedCharacterAc != null) {
            newFormData.ac = resolvedCharacterAc;
          }
          if (resolvedCharacterInitiative != null) {
            newFormData.initiative = resolvedCharacterInitiative;
          }
          if (resolvedCharacterSpeed != null) {
            newFormData.speed = resolvedCharacterSpeed;
          }
          if (resolvedCharacterProficiencyBonus != null) {
            newFormData.proficiencyBonus = resolvedCharacterProficiencyBonus;
          }
        }

        if (normalizedFormType === 'monster' && Object.keys(monsterAbilities || {}).length > 0) {
          newFormData.abilities = {
            ...(newFormData.abilities || {}),
            ...monsterAbilities
          };
          newFormData.stats = {
            ...(newFormData.stats || {}),
            str: monsterAbilities.strength,
            dex: monsterAbilities.dexterity,
            con: monsterAbilities.constitution,
            int: monsterAbilities.intelligence,
            wis: monsterAbilities.wisdom,
            cha: monsterAbilities.charisma
          };
          newFormData.strength = monsterAbilities.strength;
          newFormData.dexterity = monsterAbilities.dexterity;
          newFormData.constitution = monsterAbilities.constitution;
          newFormData.intelligence = monsterAbilities.intelligence;
          newFormData.wisdom = monsterAbilities.wisdom;
          newFormData.charisma = monsterAbilities.charisma;
        } else if (normalizedFormType !== 'monster' && Object.keys(resolvedCharacterAbilities || {}).length > 0) {
          newFormData.abilities = {
            ...(newFormData.abilities || {}),
            ...resolvedCharacterAbilities
          };
          newFormData.stats = {
            ...(newFormData.stats || {}),
            str: resolvedCharacterAbilities.strength,
            dex: resolvedCharacterAbilities.dexterity,
            con: resolvedCharacterAbilities.constitution,
            int: resolvedCharacterAbilities.intelligence,
            wis: resolvedCharacterAbilities.wisdom,
            cha: resolvedCharacterAbilities.charisma
          };
          newFormData.strength = resolvedCharacterAbilities.strength;
          newFormData.dexterity = resolvedCharacterAbilities.dexterity;
          newFormData.constitution = resolvedCharacterAbilities.constitution;
          newFormData.intelligence = resolvedCharacterAbilities.intelligence;
          newFormData.wisdom = resolvedCharacterAbilities.wisdom;
          newFormData.charisma = resolvedCharacterAbilities.charisma;
        } else if (normalizedData.abilities) {
          newFormData.strength = normalizedData.abilities.strength;
          newFormData.dexterity = normalizedData.abilities.dexterity;
          newFormData.constitution = normalizedData.abilities.constitution;
          newFormData.intelligence = normalizedData.abilities.intelligence;
          newFormData.wisdom = normalizedData.abilities.wisdom;
          newFormData.charisma = normalizedData.abilities.charisma;
        } else if (normalizedData.stats) {
          newFormData.strength = normalizedData.stats.str;
          newFormData.dexterity = normalizedData.stats.dex;
          newFormData.constitution = normalizedData.stats.con;
          newFormData.intelligence = normalizedData.stats.int;
          newFormData.wisdom = normalizedData.stats.wis;
          newFormData.charisma = normalizedData.stats.cha;
        }

        debugLog('FormData', 'Processed form data:', {
          name: newFormData.name,
          type: newFormData.type,
          hp: newFormData.hp,
          abilities: newFormData.abilities,
        });

        setFormData(newFormData);
      } catch (error) {
        if (cancelled) return;

        console.error('[TokenPropertiesPopup] Failed to normalize token data:', error);
        setFormData(prev => ({
          ...prev,
          ...token,
          type: String(token.type || prev.type || 'player').toLowerCase()
        }));
      }
    };

    normalizeTokenData();

    return () => {
      cancelled = true;
    };
  }, [token]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target) && !isDragging) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [visible, onClose, isDragging]);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    const newFormData = { ...formData };
    
    if (field.includes('.')) {
      // Handle nested fields like 'hp.current'
      const [parent, child] = field.split('.');
      newFormData[parent] = { ...newFormData[parent], [child]: value };
    } else {
      newFormData[field] = value;
    }

    if (field === 'challengeRating') {
      newFormData.cr = value;
    }

    if (field === 'creatureType') {
      newFormData.creatureTypeLabel = value;
    }
    
    // Handle special case for ability scores
    // When updating an ability score in the form, also update it in the abilities object
    const abilityMap = {
      'strength': 'str',
      'dexterity': 'dex',
      'constitution': 'con',
      'intelligence': 'int',
      'wisdom': 'wis',
      'charisma': 'cha'
    };
    
    if (Object.keys(abilityMap).includes(field)) {
      // Create/update abilities object with long-form names
      if (!newFormData.abilities) newFormData.abilities = {};
      newFormData.abilities[field] = value;
      
      // Also update short-form stats if they exist
      if (newFormData.stats) {
        newFormData.stats[abilityMap[field]] = value;
      }
    }
    
    setFormData(newFormData);
    
    // Prepare data for update
    const updateData = prepareUpdateData(newFormData);
    if (Object.keys(abilityMap).includes(field) && String(newFormData.type || '').toLowerCase() === 'monster') {
      updateData.hasCustomAbilityScores = true;
    }
    debugLog('Update', 'Preparing update for field:', field, 'with value:', value);
    
    // Auto-save changes
    if (onUpdate) {
      onUpdate(updateData);
    }
    
    // Handle token size autoscaling
    if (field === 'size' && onTokenSizeChange && token) {
      onTokenSizeChange(token.id, value);
    }
  };
  
  const handleRemoveEffect = (key) => {
    if (!isGameMaster) return;
    try {
      const currentSE = { ...(formData?.statusEffects || {}) };
      const currentMeta = { ...(formData?.statusEffectsMeta || {}) };
      if (currentSE[key]) delete currentSE[key];
      if (currentMeta[key]) delete currentMeta[key];
      setFormData(prev => ({ ...prev, statusEffects: currentSE, statusEffectsMeta: currentMeta }));
      if (onUpdate) {
        onUpdate({ statusEffects: currentSE, statusEffectsMeta: currentMeta, _gmOverride: true });
      }
    } catch (e) {
      console.error('[TokenPropertiesPopup] Failed to remove effect', key, e);
    }
  };
  
  // Prepare data for consistent update format
  const prepareUpdateData = (data) => {
    const updateData = { ...data };
    
    // Ensure HP is always in the correct format
    // We keep hp.current and hp.max in the update data
    // But also add the legacy hp and maxHp for backward compatibility
    updateData.hp = { ...data.hp };
    updateData.maxHp = data.hp.max;
    
    // Store HP object in standard format
    updateData.hpObj = { 
      current: data.hp.current, 
      max: data.hp.max 
    };
    
    // Ensure abilities are stored in standard formats
    if (!updateData.abilities) updateData.abilities = {};
    
    // Update abilities object with values from form fields
    updateData.abilities.strength = data.strength;
    updateData.abilities.dexterity = data.dexterity;
    updateData.abilities.constitution = data.constitution;
    updateData.abilities.intelligence = data.intelligence;
    updateData.abilities.wisdom = data.wisdom;
    updateData.abilities.charisma = data.charisma;
    
    // Also maintain stats object for backward compatibility
    updateData.stats = {
      str: data.strength,
      dex: data.dexterity,
      con: data.constitution,
      int: data.intelligence,
      wis: data.wisdom,
      cha: data.charisma
    };

    if (data.challengeRating != null) {
      updateData.challengeRating = data.challengeRating;
      updateData.cr = data.challengeRating;
    }

    if (data.creatureType != null) {
      updateData.creatureType = data.creatureType;
      updateData.creatureTypeLabel = data.creatureType;
    }
    
    debugLog('UpdateData', 'Prepared update data:', {
      id: updateData.id,
      name: updateData.name,
      hp: updateData.hp,
      hpObj: updateData.hpObj,
      abilities: updateData.abilities
    });
    
    return updateData;
  };

  // Handle dragging
  const handleMouseDown = (e) => {
    if (e.target.closest('.token-properties-popup__header')) {
      setIsDragging(true);
      const rect = popupRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      setPopupPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y
      });
    }
  }, [isDragging, dragOffset]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, handleMouseMove, handleMouseUp]);

  if (!visible || !token) return null;

  const isMonsterToken = String(formData.type || '').toLowerCase() === 'monster';

  const popupContent = (
    <div 
      ref={popupRef}
      className="token-properties-popup"
      style={{
        left: popupPosition.x,
        top: popupPosition.y,
      }}
      onMouseDown={handleMouseDown}
      onContextMenu={(event) => event.preventDefault()}
      role="dialog"
      aria-labelledby="token-properties-popup-title"
    >
      <div className="token-properties-popup__header">
        <div className="popup-title-section">
          <h3 id="token-properties-popup-title" className="popup-title">Token Properties</h3>
          <button 
            className={`popup-pin ${isPinned ? 'pinned' : ''}`} 
            onClick={() => {
              if (onTogglePin) {
                onTogglePin();
              } else {
                setIsPinned(!isPinned);
              }
            }}
            title={isPinned ? 'Unpin to popup' : 'Pin to sidebar'}
            aria-label={isPinned ? 'Unpin token properties' : 'Pin token properties'}
            aria-pressed={isPinned}
          >
            {isPinned ? 'Unpin' : 'Pin'}
          </button>
        </div>
        <button 
          className="popup-close" 
          onClick={() => {
            if (!isPinned) {
              onClose();
            }
          }}
          title={isPinned ? 'Popup is pinned - unpin to close' : 'Close popup'}
          aria-label={isPinned ? 'Unpin token properties before closing' : 'Close token properties'}
          disabled={isPinned}
          style={{ opacity: isPinned ? 0.5 : 1 }}
        >
          Close
        </button>
      </div>
      
      <div className="token-properties-popup__content">
        {/* Active Effects - show near the top */}
        <div className="property-section">
          <h4>Active Effects</h4>
          {(() => {
            const se = (formData && formData.statusEffects) ? formData.statusEffects : {};
            const seKeys = Object.keys(se).filter(k => !!se[k]);
            const conditions = Array.isArray(formData.conditions) ? formData.conditions : [];
            const palette = EffectColors || {}; const table = palette || {}; const map = palette.byEffect || {};
            if (seKeys.length === 0 && conditions.length === 0) {
              return (<div className="effects-empty">None</div>);
            }
            return (
              <div className="effects-list">
                {seKeys.map(key => {
                  const colorKey = map[key] || key; const color = table[colorKey] || '#4FC3F7';
                  const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                  return (
                    <span key={`se-${key}`} className="effect-pill" title={label}>
                      <span className="effect-dot" style={{ backgroundColor: color }} />
                      {label}
                      {isGameMaster && (
                        <button
                          className="effect-remove"
                          style={{ marginLeft: 6, cursor: 'pointer', background: 'transparent', border: 'none' }}
                          onClick={(e) => { e.stopPropagation(); handleRemoveEffect(key); }}
                          title={`Remove ${label}`}
                        >
                          ×
                        </button>
                      )}
                    </span>
                  );
                })}
                {conditions.length > 0 && (
                  <div className="conditions-block">
                    <div className="conditions-title">Conditions:</div>
                    <div className="conditions-pills">
                      {conditions.map(c => (
                        <span key={`cond-${c}`} className="effect-pill neutral" title={c}>
                          {String(c).replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase())}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        <TokenPresentationControls token={{ ...token, ...formData }} />
        <AuthoritativeDamageControls token={{ ...token, ...formData }} isGameMaster={isGameMaster} />
        <CreatureEquipmentControls token={token} />
        {isMonsterToken && (
          <div className="property-section monster-tabs-section">
            <div className="monster-view-tabs" role="tablist" aria-label="Monster token views">
              <button
                type="button"
                className={`monster-view-tab ${activeMonsterTab === 'sheet' ? 'active' : ''}`}
                onClick={() => setActiveMonsterTab('sheet')}
                role="tab"
                aria-selected={activeMonsterTab === 'sheet'}
              >
                Sheet
              </button>
              <button
                type="button"
                className={`monster-view-tab ${activeMonsterTab === 'edit' ? 'active' : ''}`}
                onClick={() => setActiveMonsterTab('edit')}
                role="tab"
                aria-selected={activeMonsterTab === 'edit'}
              >
                Edit
              </button>
            </div>
          </div>
        )}

        {isMonsterToken && activeMonsterTab === 'sheet' && (
          <div className="property-section monster-sheet-container">
            {(() => {
              const viewerToken = {
                ...formData,
                hp: (formData?.hp && typeof formData.hp === 'object') ? (formData.hp.current ?? 0) : (formData.hp ?? 0),
                maxHp: (formData?.hp && typeof formData.hp === 'object') ? (formData.hp.max ?? formData.maxHp ?? 0) : (formData.maxHp ?? 0),
                hpObj: formData?.hpObj || (typeof formData?.hp === 'object' ? formData.hp : { current: formData.hp ?? 0, max: formData.maxHp ?? (formData.hp ?? 0) })
              };
              return <MonsterSheetViewer token={viewerToken} />;
            })()}
          </div>
        )}

        {(!isMonsterToken || activeMonsterTab === 'edit') && (
          <>
            {/* Two-column layout: Basic Information + Combat Stats */}
            <div className="properties-grid">
              {/* Basic Information */}
              <div className="property-section">
                <h4>Basic Information</h4>
                <div className="property-row">
                  <label>Name:</label>
                  <input type="text" value={formData.name || ''} onChange={(e) => handleInputChange('name', e.target.value)} placeholder="Token name" />
                </div>
                <div className="property-row">
                  <label>{isMonsterToken ? 'Token Type:' : 'Type:'}</label>
                  <select value={formData.type || 'player'} onChange={(e) => handleInputChange('type', e.target.value)}>
                    <option value="player">Player</option>
                    <option value="npc">NPC</option>
                    <option value="monster">Monster</option>
                  </select>
                </div>
                {isMonsterToken ? (
                  <>
                    <div className="property-row">
                      <label>Creature Type:</label>
                      <input
                        type="text"
                        value={formData.creatureType || ''}
                        onChange={(e) => handleInputChange('creatureType', e.target.value)}
                        placeholder="Beast, Humanoid, Undead..."
                      />
                    </div>
                    <div className="property-row">
                      <label>Alignment:</label>
                      <input
                        type="text"
                        value={formData.alignment || ''}
                        onChange={(e) => handleInputChange('alignment', e.target.value)}
                        placeholder="Unaligned, Neutral Evil..."
                      />
                    </div>
                    <div className="property-row">
                      <label>Challenge:</label>
                      <input
                        type="text"
                        value={formData.challengeRating || formData.cr || ''}
                        onChange={(e) => handleInputChange('challengeRating', e.target.value)}
                        placeholder="1/4, 1, 5..."
                      />
                    </div>
                  </>
                ) : (
                  <div className="property-row">
                    <label>Level:</label>
                    <input type="number" value={formData.level || 1} onChange={(e) => handleInputChange('level', parseInt(e.target.value, 10) || 1)} min="1" max="20" />
                  </div>
                )}
                <div className="property-row">
                  <label>Size:</label>
                  <select value={formData.size || 'Medium'} onChange={(e) => handleInputChange('size', e.target.value)}>
                    <option value="Tiny">Tiny</option>
                    <option value="Small">Small</option>
                    <option value="Medium">Medium</option>
                    <option value="Large">Large</option>
                    <option value="Huge">Huge</option>
                    <option value="Gargantuan">Gargantuan</option>
                  </select>
                </div>
                <div className="property-row">
                  <label>Grid Position:</label>
                  <div className="grid-coordinates">
                    <span>({formData.gridX || 0}, {formData.gridY || 0})</span>
                    <small>Grid coordinates (0,0 = upper left)</small>
                  </div>
                </div>
              </div>

              {/* Combat Stats */}
              <div className="property-section">
                <h4>Combat Stats</h4>
                <div className="property-row">
                  <label>Hit Points:</label>
                  <div className="hp-inputs">
                    <input type="number" value={formData.hp.current || 0} onChange={(e) => handleInputChange('hp.current', parseInt(e.target.value) || 0)} placeholder="Current" min="0" />
                    <span>/</span>
                    <input type="number" value={formData.hp.max || 0} onChange={(e) => handleInputChange('hp.max', parseInt(e.target.value) || 0)} placeholder="Max" min="1" />
                  </div>
                </div>
                <div className="property-row">
                  <label>Armor Class:</label>
                  <input type="number" value={formData.ac || 10} onChange={(e) => handleInputChange('ac', parseInt(e.target.value) || 10)} min="1" max="30" />
                </div>
                <div className="property-row">
                  <label>Initiative:</label>
                  <input type="number" value={formData.initiative || 0} onChange={(e) => handleInputChange('initiative', parseInt(e.target.value) || 0)} min="-10" max="30" />
                </div>
                <div className="property-row">
                  <label>Speed:</label>
                  <input type="number" value={formData.speed ?? 30} onChange={(e) => handleInputChange('speed', parseInt(e.target.value, 10) || 0)} min="0" max="120" />
                  <span className="unit">ft</span>
                </div>
              </div>
            </div>

            {/* Ability Scores */}
            <div className="property-section">
              <h4>Ability Scores</h4>
              
              <div className="abilities-grid">
                {[
                  { short: 'str', full: 'strength' },
                  { short: 'dex', full: 'dexterity' },
                  { short: 'con', full: 'constitution' },
                  { short: 'int', full: 'intelligence' },
                  { short: 'wis', full: 'wisdom' },
                  { short: 'cha', full: 'charisma' }
                ].map(ability => (
                  <div key={ability.short} className="ability-input">
                    <label>{ability.short.toUpperCase()}:</label>
                    <input
                      type="number"
                      value={formData[ability.full] || 10}
                      onChange={(e) => handleInputChange(ability.full, parseInt(e.target.value) || 10)}
                      min="1"
                      max="30"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="property-section">
              <h4>Notes</h4>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Add notes about this token..."
                rows="3"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (typeof document === 'undefined' || !document.body) {
    return popupContent;
  }

  return createPortal(popupContent, document.body);
};

TokenPropertiesPopup.propTypes = {
  visible: PropTypes.bool.isRequired,
  token: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  isGameMaster: PropTypes.bool,
  position: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  })
};

export default TokenPropertiesPopup;
